import {
  AnalyticsItemCategory,
  CURRENCY_CODE_EUR,
  EcommerceEventTypeV4,
  pushToDataLayer,
} from '../../common/analytics';
import { fetchOnlineModelForCode } from '../../common/fetch';
import {
  getAddToCartAnalyticsEvent,
  getEcommerceItems,
  getItemVariant,
  getRemoveFromCartAnalyticsEvent,
  getSubscriptionPurchaseAnalyticsEvent,
  isSubscriptionProduct,
} from '../../common/utils/analyticsUtils';
import { getManufacturer } from '../../common/utils/cartProductUtils';
import { centsToEuros } from '../../common/utils/priceUtils';
import {
  PaymentTypeEnum,
  createEcommerceItemFromCommercialProducts,
  createEcommerceItemFromShoppingCartItem,
  getProductPriceData,
} from '../../selfservice/common/googleEcommerceEvent';
import {
  getCommercialProductForGuid,
  getOnlineModelForBasketItem,
  isSalesProductModel,
} from '../ShoppingBasket/shoppingBasketUtils';
import type {
  CheckoutEcommerceEvent,
  EcommerceAddToCartEventV4,
  EcommerceCheckoutStepEvent,
  EcommerceItem,
  EcommercePurchaseEventV4,
  EcommerceRemoveFromCartEventV4,
} from '../../common/analytics';
import type { AddedAddon } from '../../common/types/addon';
import type { ShoppingCartAddOn, ShoppingCartItemForCheckout } from '../../common/types/checkout';
import type { ConfiguredOffer } from '../../common/types/commercialProduct';
import type { BasketItem } from '../../common/types/shoppingBasket';
import type { OnlineModel } from '../../generated/api/onlineModel';

export const mapShoppingCartAddonsToAddedAddons = (shoppingCardAddons?: Array<ShoppingCartAddOn>): AddedAddon[] =>
  shoppingCardAddons?.map(shoppingCartAddon => ({
    addOnCode: shoppingCartAddon.addOnCode,
    addOnAssociationCode: shoppingCartAddon.addOnAssociationCode,
    displayName: shoppingCartAddon.addOnProductName,
    oneTimePrice: shoppingCartAddon.addOnOneTimeCharge,
    monthlyPrice: shoppingCartAddon.addOnMonthlyRecurringCharge,
  })) || [];

const getCheckoutEvent = async (
  shoppingCartItems: ShoppingCartItemForCheckout[],
  eventType: EcommerceCheckoutStepEvent,
  onlineModels?: OnlineModel[]
): Promise<CheckoutEcommerceEvent> => {
  const itemPromises = shoppingCartItems.map(async item => {
    const onlineModel =
      onlineModels?.find(model => model.onlineModelCode === item.onlineModelCode) ??
      // TODO device checkout uses currently Redux based online models and Redux store might not include
      // some sales product models. After de-reduxation, there should be no more issues.
      (await fetchOnlineModelForCode(item.onlineModelCode));

    if (!onlineModel) {
      return [];
    }

    const { onlineModelName, offers } = onlineModel;
    const offer = offers.find(o => o.offerCode === item.offerCode);
    if (!offer) {
      return [];
    }

    return getEcommerceItems(
      onlineModelName,
      offer,
      item.quantity,
      eventType,
      mapShoppingCartAddonsToAddedAddons(item.selectedAddOns)
    );
  });

  // Wait for all promises to resolve and flatten the results
  const resolvedItems = (await Promise.all(itemPromises)).flat();

  if (eventType === EcommerceEventTypeV4.ADD_TO_CART || eventType === EcommerceEventTypeV4.REMOVE_FROM_CART) {
    return {
      event: eventType,
      ecommerce: {
        currency: CURRENCY_CODE_EUR,
        items: resolvedItems,
      },
    };
  }

  return {
    event: eventType,
    ecommerce: {
      items: resolvedItems,
    },
  };
};

export const createDevicePurchaseEvent = (
  orderId: string,
  revenueInCents: number,
  taxInCents: number,
  shippingInCents: number,
  productData: EcommerceItem[],
  paymentType: PaymentTypeEnum
): EcommercePurchaseEventV4 => ({
  ecommerce: {
    currency: CURRENCY_CODE_EUR,
    items: productData,
    shipping: shippingInCents ? centsToEuros(shippingInCents) : 0,
    tax: parseFloat((taxInCents ? centsToEuros(taxInCents) : 0).toFixed(2)),
    // eslint-disable-next-line @typescript-eslint/naming-convention
    transaction_id: orderId,
    value: revenueInCents ? centsToEuros(revenueInCents) : 0,
  },
  event: EcommerceEventTypeV4.PURCHASE,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  payment_type: paymentType,
});

export const handleCheckoutAnalytics = async (
  cartItems: ShoppingCartItemForCheckout[],
  eventType: EcommerceCheckoutStepEvent,
  models?: OnlineModel[]
): Promise<void> => {
  if (!cartItems.length) {
    return;
  }

  const salesProducts = cartItems.filter(item => item.category === AnalyticsItemCategory.SALES_PRODUCT);
  const otherProducts = cartItems.filter(item => item.category !== AnalyticsItemCategory.SALES_PRODUCT);

  const salesEvent = salesProducts.length > 0 ? await getCheckoutEvent(salesProducts, eventType, models) : undefined;
  const ecommerceItems =
    otherProducts.length > 0 ? otherProducts.map(item => createEcommerceItemFromShoppingCartItem(item)) : [];

  // Combine all items
  const allEcommerceItems: EcommerceItem[] = [...(salesEvent?.ecommerce?.items ?? []), ...ecommerceItems];

  // Aggregate by item_id
  const itemMap = new Map<string, EcommerceItem>();
  for (const item of allEcommerceItems) {
    if (itemMap.has(item.item_id)) {
      const existing = itemMap.get(item.item_id)!;
      existing.quantity = (existing.quantity ?? 1) + (item.quantity ?? 1);
    } else {
      itemMap.set(item.item_id, { ...item });
    }
  }

  const combinedItems = Array.from(itemMap.values());

  if (combinedItems.length > 0) {
    pushToDataLayer({
      event: eventType,
      ecommerce: {
        items: combinedItems,
      },
    });
  }
};

const combinePurchaseEvents = (events: EcommercePurchaseEventV4[]): EcommercePurchaseEventV4 => {
  if (events.length === 1) {
    return events[0];
  }

  const base = structuredClone(events[0]);
  base.ecommerce.items = events.flatMap(e => e.ecommerce.items);
  base.ecommerce.value = Number(events.reduce((sum, e) => sum + (e.ecommerce.value || 0), 0).toFixed(2));
  base.ecommerce.tax = Number(events.reduce((sum, e) => sum + (e.ecommerce.tax || 0), 0).toFixed(2));
  base.ecommerce.shipping = Number(events.reduce((sum, e) => sum + (e.ecommerce.shipping || 0), 0).toFixed(2));
  // Use the first non-empty payment_type (all should be the same)
  base.payment_type = events.find(e => e.payment_type)?.payment_type || base.payment_type;

  return base;
};

export const handleOrderPurchaseAnalytics = (items: ConfiguredOffer[], orderId: string): void => {
  const subscriptionProducts = items.filter(item => isSubscriptionProduct(item?.onlineModelName));
  const otherProducts = items.filter(item => !isSubscriptionProduct(item?.onlineModelName));
  const purchaseEvents: EcommercePurchaseEventV4[] = [];

  if (subscriptionProducts.length) {
    subscriptionProducts.forEach(product =>
      purchaseEvents.push(getSubscriptionPurchaseAnalyticsEvent(product, orderId))
    );
  }

  if (otherProducts.length) {
    const priceData = getProductPriceData(otherProducts);
    purchaseEvents.push(
      createDevicePurchaseEvent(
        orderId,
        priceData.totalRevenue,
        priceData.totalTax,
        priceData.totalShipping,
        priceData.eCommerceItem,
        PaymentTypeEnum.INVOICE
      )
    );
  }

  if (purchaseEvents.length) {
    // Only one purchase event per order is allowed
    pushToDataLayer(combinePurchaseEvents(purchaseEvents));
  }
};

const createAddToCartEvent = (ecommerceItem: EcommerceItem): EcommerceAddToCartEventV4 => ({
  event: EcommerceEventTypeV4.ADD_TO_CART,
  ecommerce: {
    currency: CURRENCY_CODE_EUR,
    items: [ecommerceItem],
  },
});

const createRemoveFromCartEvent = (ecommerceItem: EcommerceItem): EcommerceRemoveFromCartEventV4 => ({
  event: EcommerceEventTypeV4.REMOVE_FROM_CART,
  ecommerce: {
    currency: CURRENCY_CODE_EUR,
    items: [ecommerceItem],
  },
});

export const handleAnalyticsEventForAddOrRemoveItem = (
  basketItem: BasketItem | undefined,
  newQuantity: number,
  onlineModels: OnlineModel[]
): void => {
  if (!basketItem) {
    return;
  }
  const onlineModelForItem = getOnlineModelForBasketItem(onlineModels, basketItem);
  if (!onlineModelForItem) {
    return;
  }
  const offer = onlineModelForItem?.offers.find(o => o.offerCode === basketItem.offer.guid);
  if (!offer) {
    return;
  }

  const delta = newQuantity - basketItem.quantity;
  if (isSalesProductModel(onlineModelForItem)) {
    const salesProductEvent =
      delta > 0
        ? getAddToCartAnalyticsEvent(onlineModelForItem.onlineModelName, offer, Math.abs(delta))
        : getRemoveFromCartAnalyticsEvent(onlineModelForItem.onlineModelName, offer, Math.abs(delta));
    pushToDataLayer(salesProductEvent);
  } else {
    const commercialProduct = getCommercialProductForGuid(basketItem.offer.commercialProductGuid, onlineModelForItem);
    if (!commercialProduct) {
      return;
    }

    const ecommerceItem = createEcommerceItemFromCommercialProducts(
      onlineModelForItem.onlineModelCode,
      onlineModelForItem.category ?? commercialProduct.productSubType ?? commercialProduct.productType,
      onlineModelForItem.onlineModelName,
      getItemVariant(EcommerceEventTypeV4.ADD_TO_CART, commercialProduct.commercialProductName) ??
        commercialProduct.commercialProductName,
      [commercialProduct],
      Math.abs(delta),
      getManufacturer(onlineModelForItem, commercialProduct.commercialProductName)
    );

    const deviceEvent = delta > 0 ? createAddToCartEvent(ecommerceItem) : createRemoveFromCartEvent(ecommerceItem);

    if (deviceEvent) {
      pushToDataLayer(deviceEvent);
    }
  }
};

export const handleAnalyticsEventForAddonChange = (
  basketItem: BasketItem | undefined,
  productIndex: number,
  newAddons: string[],
  onlineModels: OnlineModel[]
): void => {
  if (!basketItem?.offer.addOns?.[productIndex]) {
    return;
  }

  const oldAddons = basketItem.offer.addOns[productIndex].addOnCodes || [];
  const removedAddons = oldAddons.filter(addon => !newAddons.includes(addon));

  if (removedAddons.length === 0) {
    return;
  }

  const onlineModelForItem = getOnlineModelForBasketItem(onlineModels, basketItem);
  if (!onlineModelForItem) {
    return;
  }

  const commercialProduct = getCommercialProductForGuid(basketItem.offer.commercialProductGuid, onlineModelForItem);
  if (!commercialProduct) {
    return;
  }

  const ecommerceItem = createEcommerceItemFromCommercialProducts(
    onlineModelForItem.onlineModelCode,
    onlineModelForItem.category ?? commercialProduct.productSubType ?? commercialProduct.productType,
    onlineModelForItem.onlineModelName,
    getItemVariant(EcommerceEventTypeV4.REMOVE_FROM_CART, commercialProduct.commercialProductName) ??
      commercialProduct.commercialProductName,
    [commercialProduct],
    removedAddons.length,
    getManufacturer(onlineModelForItem, commercialProduct.commercialProductName)
  );

  pushToDataLayer({
    event: EcommerceEventTypeV4.REMOVE_ADDITIONAL_SERVICES,
    ecommerce: {
      currency: CURRENCY_CODE_EUR,
      items: [ecommerceItem],
    },
  });
};
